import React, { useState } from 'react';
import { Clock, TrendingUp, CheckCircle, BarChart3, User } from 'lucide-react';
import { getStatistiquesTechnicien, typesIntervention, getTechniciens, getUserById } from '../../data/mockData';

function StatistiquesTechnicien({ user }) {
  const techniciens = getTechniciens();
  const [selectedTechnicien, setSelectedTechnicien] = useState(techniciens[0]?.id || '');
  const [periode, setPeriode] = useState('mois');
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Vérifier que l'utilisateur est admin
  if (user.role !== 'admin') {
    return (
      <div className="page-container">
        <div className="alert alert-error">
          Vous n'avez pas accès à cette page.
        </div>
      </div>
    );
  }

  const months = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  const years = [];
  const currentYear = new Date().getFullYear();
  for (let y = currentYear; y >= currentYear - 2; y--) {
    years.push(y);
  }

  // Fonction pour calculer la période du 27 au 26 du mois suivant
  const getMonthPeriod = (month, year) => {
    let debutMonth = month - 1;
    let debutYear = year;
    if (debutMonth < 0) {
      debutMonth = 11;
      debutYear = year - 1;
    }
    const debut = new Date(debutYear, debutMonth, 27);
    const fin = new Date(year, month, 26);
    
    return {
      debut: debut.toISOString().split('T')[0],
      fin: fin.toISOString().split('T')[0]
    };
  };

  const getDateRange = () => {
    const today = new Date();
    let debut, fin;

    switch (periode) {
      case 'jour':
        debut = fin = today.toISOString().split('T')[0];
        break;
      case 'semaine':
        const firstDay = new Date(today);
        firstDay.setDate(today.getDate() - 7);
        debut = firstDay.toISOString().split('T')[0];
        fin = today.toISOString().split('T')[0];
        break;
      case 'mois':
        const monthPeriod = getMonthPeriod(selectedMonth, selectedYear);
        debut = monthPeriod.debut;
        fin = monthPeriod.fin;
        break;
      case 'personnalise':
        debut = dateDebut;
        fin = dateFin;
        break;
      default:
        debut = fin = today.toISOString().split('T')[0];
    }

    return { debut, fin };
  };

  // Fonction pour déterminer la couleur de performance
  const getPerformanceColor = (code, tempsMoyen) => {
    const type = typesIntervention.find(t => t.code === code);
    if (!type || !type.dureeEstimee) return 'neutral';
    
    if (tempsMoyen <= type.dureeEstimee) {
      return 'good'; // Vert - dans les temps
    } else {
      return 'bad'; // Rouge - dépassement
    }
  };

  const { debut, fin } = getDateRange();
  const stats = selectedTechnicien ? getStatistiquesTechnicien(parseInt(selectedTechnicien), debut, fin) : null;
  const technicienInfo = selectedTechnicien ? getUserById(parseInt(selectedTechnicien)) : null;

  const formatDuree = (minutes) => {
    const heures = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${heures}h${mins.toString().padStart(2, '0')}`;
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>Statistiques par technicien</h1>
          <p className="text-muted">Analyse des performances individuelles</p>
        </div>
      </div>

      {/* Sélection du technicien */}
      <div className="technicien-selector-card">
        <div className="technicien-selector-header">
          <User size={20} />
          <span>Sélectionner un technicien</span>
        </div>
        <div className="technicien-selector-buttons">
          {techniciens.map(tech => (
            <button
              key={tech.id}
              className={`technicien-btn ${selectedTechnicien === tech.id ? 'active' : ''}`}
              onClick={() => setSelectedTechnicien(tech.id)}
            >
              <div className="user-avatar user-avatar-sm">
                {tech.prenom[0]}{tech.nom[0]}
              </div>
              <span>{tech.prenom} {tech.nom}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="stats-filters">
        <div className="periode-selector">
          <button
            className={`btn ${periode === 'jour' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setPeriode('jour')}
          >
            Aujourd'hui
          </button>
          <button
            className={`btn ${periode === 'semaine' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setPeriode('semaine')}
          >
            7 derniers jours
          </button>
          <button
            className={`btn ${periode === 'mois' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setPeriode('mois')}
          >
            Mois
          </button>
          <button
            className={`btn ${periode === 'personnalise' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setPeriode('personnalise')}
          >
            Personnalisé
          </button>
        </div>

        {periode === 'mois' && (
          <div className="month-selector">
            <div className="form-group">
              <label>Mois</label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              >
                {months.map((month, index) => (
                  <option key={index} value={index}>{month}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Année</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              >
                {years.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {periode === 'personnalise' && (
          <div className="date-range-selector">
            <div className="form-group">
              <label>Du</label>
              <input
                type="date"
                value={dateDebut}
                onChange={(e) => setDateDebut(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Au</label>
              <input
                type="date"
                value={dateFin}
                onChange={(e) => setDateFin(e.target.value)}
              />
            </div>
          </div>
        )}
      </div>

      {/* Affichage de la période */}
      {periode === 'mois' && (
        <div className="periode-display">
          <p>
            <strong>Période : </strong>
            Du 27 {months[(selectedMonth - 1 + 12) % 12].toLowerCase()} au 26 {months[selectedMonth].toLowerCase()} {selectedYear}
          </p>
        </div>
      )}

      {!selectedTechnicien ? (
        <div className="empty-state">
          <User size={48} className="icon-muted" />
          <p>Sélectionnez un technicien pour voir ses statistiques</p>
        </div>
      ) : (
        <>

          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon stat-icon-primary">
                <CheckCircle size={32} />
              </div>
              <div className="stat-content">
                <p className="stat-label">Interventions réalisées</p>
                <h2 className="stat-value">{stats?.nombreInterventions || 0}</h2>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon stat-icon-success">
                <Clock size={32} />
              </div>
              <div className="stat-content">
                <p className="stat-label">Temps moyen par intervention</p>
                <h2 className="stat-value">
                  {stats?.nombreInterventions > 0 
                    ? formatDuree(stats.tempsMoyenIntervention)
                    : '-'}
                </h2>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon stat-icon-info">
                <TrendingUp size={32} />
              </div>
              <div className="stat-content">
                <p className="stat-label">Temps total travaillé</p>
                <h2 className="stat-value">{formatDuree(stats?.tempsTotalTravaille || 0)}</h2>
              </div>
            </div>
          </div>

          <div className="stats-details">
            <div className="stats-card">
              <div className="stats-card-header">
                <BarChart3 size={24} />
                <h2>Interventions par type</h2>
              </div>
              <div className="stats-table">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Nombre</th>
                      <th>Temps moyen</th>
                      <th>Objectif</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!stats || Object.keys(stats.nombreParType).length === 0 ? (
                      <tr>
                        <td colSpan="4" className="text-center text-muted">
                          Aucune intervention sur cette période
                        </td>
                      </tr>
                    ) : (
                      Object.keys(stats.nombreParType).map(code => {
                        const type = typesIntervention.find(t => t.code === code);
                        const performanceColor = getPerformanceColor(code, stats.tempsParType[code]);
                        
                        return (
                          <tr key={code}>
                            <td>
                              {type && (
                                <span 
                                  className="type-badge" 
                                  style={{ backgroundColor: type.couleur }}
                                >
                                  {code}
                                </span>
                              )}
                              <span className="ml-2">{type?.libelle}</span>
                            </td>
                            <td><strong>{stats.nombreParType[code]}</strong></td>
                            <td>
                              <span className={`performance-indicator ${performanceColor}`}>
                                {performanceColor === 'good' && '✓ '}
                                {performanceColor === 'bad' && '⚠️ '}
                                {formatDuree(stats.tempsParType[code])}
                              </span>
                            </td>
                            <td className="text-muted text-sm">
                              {type?.dureeEstimee ? `Objectif: ${formatDuree(type.dureeEstimee)}` : '-'}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default StatistiquesTechnicien;
